/**
 * ルーターのガードで、「取得に失敗した」と「正常に取得して、未契約・未登録・未連携と分かった」を区別する（指示書 A4、A-1③-1）。
 *
 * 以前は、DBやAPIの取得エラーを無視して、結果を空として扱っていた。そのため、通信の不調だけで
 * 「未契約→マイページ」「初期登録未完了→Onboarding」「未ログイン→ログイン画面」へ送られていた。
 * 取得エラーは GuardAccessError として投げ、ガードは回復画面へ出す。
 */

export class GuardAccessError extends Error {
  /**
   * @param {string} reason 何の取得に失敗したか（観測用。個人情報を入れない）
   * @param {unknown} [cause]
   */
  constructor(reason, cause) {
    super(`guard access check failed: ${reason}`)
    this.name = 'GuardAccessError'
    this.reason = reason
    this.cause = cause
  }
}

/**
 * supabase.auth.getUser() の結果を分類する。
 *   user       … ログイン中
 *   no_session … セッションが無い・無効（期限切れ・失効）と確認できた → ログイン画面へ
 *   error      … 認証サーバーへの通信失敗など、確認できなかった → 未ログインと断定しない
 */
export function classifyGetUserResult(result) {
  const user = result?.data?.user
  if (user) return 'user'
  const error = result?.error
  if (!error) return 'no_session'
  const name = String(error.name || '')
  const status = Number(error.status)
  const message = String(error.message || '')
  if (name === 'AuthSessionMissingError' || /auth session missing/i.test(message)) return 'no_session'
  if (status === 401 || status === 403) return 'no_session'
  return 'error'
}

/** DB の取得結果（{ data, error }）に error があれば GuardAccessError を投げる */
export function assertQueryOk(result, reason) {
  if (result?.error) throw new GuardAccessError(reason, result.error)
  return result
}

/**
 * ガードで例外になったときの行き先。
 * 保護されたページとログイン入口は回復画面へ（ログイン画面へ戻して「未ログイン」に見せない）。
 * それ以外の公開ページ（パスワード再設定・登録の途中など）は、そのまま表示する。
 * @returns {undefined | { name: 'ConnectionError', query: { to: string } }}
 */
export function guardErrorExit(to, toReturnPath) {
  const needsRecovery = Boolean(to?.meta?.requiresAuth) || to?.path === '/'
  if (!needsRecovery) return undefined
  return { name: 'ConnectionError', query: { to: toReturnPath(to?.fullPath || '/top') } }
}
